/**
 * /admin/quizz — Editor dos quizzes (Quiz Dor e Quiz Pós).
 *
 * Permite editar, sem tocar em código e sem refazer o fluxo de captura:
 *  - textos das notas (Ouro / Prata / Bronze) do Quiz Dor;
 *  - perguntas, alternativas, resposta correta e comentário do gabarito;
 *  - perguntas e alternativas do Quiz Pós (os pesos por pós ficam fixos).
 *
 * O conteúdo é salvo em site_config (`lp.quizz-*.content`) e as LPs leem em
 * tempo real via /api/site-config.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ExternalLink, Loader2, RotateCcw, Save, Trash2, Plus } from "lucide-react";

import { AdminGate } from "@/components/admin/AdminGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import {
  getQuizzContent,
  saveQuizzContent,
  resetQuizzContent,
} from "@/lib/quizz/admin.functions";
import type { DorContent, PosContent } from "@/lib/quizz/content";

export const Route = createFileRoute("/admin/quizz")({
  head: () => ({
    meta: [
      { title: "Admin — Editor do Quizz" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => (
    <AdminGate route="/admin/quizz">
      <QuizzEditorPage />
    </AdminGate>
  ),
});

function QuizzEditorPage() {
  const load = useServerFn(getQuizzContent);
  const save = useServerFn(saveQuizzContent);
  const reset = useServerFn(resetQuizzContent);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin", "quizz-content"],
    queryFn: () => load(),
  });

  const [dor, setDor] = useState<DorContent | null>(null);
  const [pos, setPos] = useState<PosContent | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (data) {
      setDor(structuredClone(data.dor));
      setPos(structuredClone(data.pos));
    }
  }, [data]);

  async function handleSave(quiz: "dor" | "pos") {
    if (quiz === "dor" && !dor) return;
    if (quiz === "pos" && !pos) return;
    setBusy(true);
    try {
      await save({
        data:
          quiz === "dor"
            ? { quiz: "dor", content: dor as DorContent }
            : { quiz: "pos", content: pos as PosContent },
      });
      toast.success("Alterações publicadas — já valem na LP.");
      await refetch();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Falha ao salvar");
    } finally {
      setBusy(false);
    }
  }

  async function handleReset(quiz: "dor" | "pos") {
    if (!window.confirm("Restaurar os textos originais deste quiz?")) return;
    setBusy(true);
    try {
      await reset({ data: { quiz } });
      toast.success("Conteúdo original restaurado.");
      await refetch();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Falha ao restaurar");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen px-4 py-8 sm:py-12">
      <div className="mx-auto max-w-4xl">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
          <div>
            <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white">
              <ArrowLeft className="h-4 w-4" /> Voltar ao painel
            </Link>
            <h1 className="mt-3 text-2xl font-bold text-white">Editor do Quizz</h1>
            <p className="mt-1 text-sm text-white/60">
              Altere as copys das notas e das perguntas. Ao salvar, a mudança vale na hora — sem
              precisar refazer o fluxo de captura.
            </p>
          </div>
          <div className="flex gap-2">
            <Button asChild variant="outline" size="sm" className="border-white/10 bg-white/5 text-white hover:bg-white/10 hover:text-white">
              <a href="/quizz-dor-pos-todas" target="_blank" rel="noreferrer">
                Quiz Dor <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
              </a>
            </Button>
            <Button asChild variant="outline" size="sm" className="border-white/10 bg-white/5 text-white hover:bg-white/10 hover:text-white">
              <a href="/quizz-pos-todas" target="_blank" rel="noreferrer">
                Quiz Pós <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
              </a>
            </Button>
          </div>
        </div>

        {isLoading || !dor || !pos ? (
          <div className="flex items-center gap-2 text-white/70">
            <Loader2 className="h-4 w-4 animate-spin" /> Carregando conteúdo...
          </div>
        ) : (
          <Tabs defaultValue="dor">
            <TabsList className="bg-white/5">
              <TabsTrigger value="dor">Quiz Dor</TabsTrigger>
              <TabsTrigger value="pos">Quiz Pós</TabsTrigger>
            </TabsList>

            {/* ---------------- Quiz Dor ---------------- */}
            <TabsContent value="dor" className="mt-6 space-y-6">
              <Card title="Textos das notas" desc="Mensagem exibida no resultado de cada nota.">
                {(["ouro", "prata", "bronze"] as const).map((k) => (
                  <div key={k} className="rounded-xl border border-white/10 bg-white/5 p-4">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="text-xs font-semibold uppercase tracking-widest text-admin-accent">
                        {k}
                      </span>
                      <div className="flex-1 min-w-[180px]">
                        <Label className="text-xs text-white/60">Título da nota</Label>
                        <Input
                          value={dor.tiers[k].label}
                          onChange={(e) =>
                            setDor({
                              ...dor,
                              tiers: { ...dor.tiers, [k]: { ...dor.tiers[k], label: e.target.value } },
                            })
                          }
                          className="mt-1 border-white/10 bg-white/5 text-white"
                        />
                      </div>
                    </div>
                    <div className="mt-3">
                      <Label className="text-xs text-white/60">Mensagem</Label>
                      <Textarea
                        rows={6}
                        value={dor.tiers[k].message}
                        onChange={(e) =>
                          setDor({
                            ...dor,
                            tiers: { ...dor.tiers, [k]: { ...dor.tiers[k], message: e.target.value } },
                          })
                        }
                        className="mt-1 border-white/10 bg-white/5 text-white"
                      />
                    </div>
                  </div>
                ))}
                <p className="text-xs text-white/50">
                  Faixas atuais: acima de 80% de acertos = {dor.tiers.ouro.label} · 40% a 80% ={" "}
                  {dor.tiers.prata.label} · abaixo de 40% = {dor.tiers.bronze.label}.
                </p>
              </Card>

              <Card title="Perguntas do quiz" desc="Marque a alternativa correta e ajuste o comentário do gabarito.">
                {dor.questions.map((q, qi) => (
                  <div key={q.id} className="rounded-xl border border-white/10 bg-white/5 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <span className="text-xs font-semibold uppercase tracking-widest text-white/50">
                        Pergunta {qi + 1}
                      </span>
                      {dor.questions.length > 1 && (
                        <button
                          type="button"
                          onClick={() =>
                            setDor({ ...dor, questions: dor.questions.filter((_, i) => i !== qi) })
                          }
                          className="text-white/40 transition hover:text-red-400"
                          title="Remover pergunta"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <Textarea
                      rows={2}
                      value={q.title}
                      onChange={(e) => {
                        const qs = [...dor.questions];
                        qs[qi] = { ...q, title: e.target.value };
                        setDor({ ...dor, questions: qs });
                      }}
                      className="mt-2 border-white/10 bg-white/5 text-white"
                    />

                    <div className="mt-3 space-y-2">
                      {q.options.map((opt, oi) => (
                        <div key={oi} className="flex items-start gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              const qs = [...dor.questions];
                              qs[qi] = { ...q, correct: oi };
                              setDor({ ...dor, questions: qs });
                            }}
                            title="Marcar como correta"
                            className={`mt-1.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border text-[10px] font-bold transition ${
                              q.correct === oi
                                ? "border-emerald-400 bg-emerald-400 text-[#0b0a1f]"
                                : "border-white/20 text-white/50 hover:border-emerald-400/60"
                            }`}
                          >
                            {String.fromCharCode(97 + oi)}
                          </button>
                          <Textarea
                            rows={2}
                            value={opt}
                            onChange={(e) => {
                              const qs = [...dor.questions];
                              const options = [...q.options];
                              options[oi] = e.target.value;
                              qs[qi] = { ...q, options };
                              setDor({ ...dor, questions: qs });
                            }}
                            className="border-white/10 bg-white/5 text-white"
                          />
                          {q.options.length > 2 && (
                            <button
                              type="button"
                              onClick={() => {
                                const qs = [...dor.questions];
                                const options = q.options.filter((_, i) => i !== oi);
                                qs[qi] = {
                                  ...q,
                                  options,
                                  correct: Math.min(q.correct, options.length - 1),
                                };
                                setDor({ ...dor, questions: qs });
                              }}
                              className="mt-1.5 text-white/40 transition hover:text-red-400"
                              title="Remover alternativa"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      ))}
                      {q.options.length < 8 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const qs = [...dor.questions];
                            qs[qi] = { ...q, options: [...q.options, "Nova alternativa"] };
                            setDor({ ...dor, questions: qs });
                          }}
                          className="text-white/60 hover:text-white"
                        >
                          <Plus className="h-3.5 w-3.5" /> Alternativa
                        </Button>
                      )}
                    </div>

                    <div className="mt-3">
                      <Label className="text-xs text-white/60">Comentário do gabarito</Label>
                      <Textarea
                        rows={2}
                        value={q.explanation}
                        onChange={(e) => {
                          const qs = [...dor.questions];
                          qs[qi] = { ...q, explanation: e.target.value };
                          setDor({ ...dor, questions: qs });
                        }}
                        className="mt-1 border-white/10 bg-white/5 text-white"
                      />
                    </div>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setDor({
                      ...dor,
                      questions: [
                        ...dor.questions,
                        {
                          id: `q${Date.now()}`,
                          title: "Nova pergunta",
                          options: ["Alternativa a", "Alternativa b"],
                          correct: 0,
                          explanation: "",
                        },
                      ],
                    })
                  }
                  className="border-white/10 bg-white/5 text-white hover:bg-white/10 hover:text-white"
                >
                  <Plus className="h-4 w-4" /> Adicionar pergunta
                </Button>
              </Card>

              <Actions busy={busy} onSave={() => handleSave("dor")} onReset={() => handleReset("dor")} />
            </TabsContent>

            {/* ---------------- Quiz Pós ---------------- */}
            <TabsContent value="pos" className="mt-6 space-y-6">
              <Card
                title="Perguntas do quiz de perfil"
                desc="Edite os textos. Os pesos por pós-graduação de cada alternativa continuam iguais."
              >
                {pos.questions.map((q, qi) => (
                  <div key={q.id} className="rounded-xl border border-white/10 bg-white/5 p-4">
                    <span className="text-xs font-semibold uppercase tracking-widest text-white/50">
                      Pergunta {qi + 1}
                    </span>
                    <Textarea
                      rows={2}
                      value={q.title}
                      onChange={(e) => {
                        const qs = [...pos.questions];
                        qs[qi] = { ...q, title: e.target.value };
                        setPos({ questions: qs });
                      }}
                      className="mt-2 border-white/10 bg-white/5 text-white"
                    />
                    <div className="mt-2">
                      <Label className="text-xs text-white/60">Subtítulo (opcional)</Label>
                      <Input
                        value={q.subtitle ?? ""}
                        onChange={(e) => {
                          const qs = [...pos.questions];
                          qs[qi] = { ...q, subtitle: e.target.value };
                          setPos({ questions: qs });
                        }}
                        className="mt-1 border-white/10 bg-white/5 text-white"
                      />
                    </div>
                    <div className="mt-3 space-y-2">
                      {q.options.map((opt, oi) => (
                        <div key={oi}>
                          <Textarea
                            rows={2}
                            value={opt.label}
                            onChange={(e) => {
                              const qs = [...pos.questions];
                              const options = [...q.options];
                              options[oi] = { ...opt, label: e.target.value };
                              qs[qi] = { ...q, options };
                              setPos({ questions: qs });
                            }}
                            className="border-white/10 bg-white/5 text-white"
                          />
                          <p className="mt-1 text-[11px] text-white/40">
                            Pesos: {Object.entries(opt.weights).map(([k, v]) => `${k} +${v}`).join(" · ") || "—"}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </Card>

              <Actions busy={busy} onSave={() => handleSave("pos")} onReset={() => handleReset("pos")} />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}

function Card({ title, desc, children }: { title: string; desc: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-sm">
      <h2 className="text-lg font-bold text-white">{title}</h2>
      <p className="mt-1 text-sm text-white/60">{desc}</p>
      <div className="mt-4 space-y-4">{children}</div>
    </section>
  );
}

function Actions({ busy, onSave, onReset }: { busy: boolean; onSave: () => void; onReset: () => void }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Button
        onClick={onSave}
        disabled={busy}
        className="bg-[#f5b022] font-semibold text-[#13123a] hover:bg-[#e09f17]"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salvar e aplicar
      </Button>
      <Button
        variant="outline"
        onClick={onReset}
        disabled={busy}
        className="border-white/10 bg-white/5 text-white hover:bg-white/10 hover:text-white"
      >
        <RotateCcw className="h-4 w-4" /> Restaurar original
      </Button>
    </div>
  );
}
